<template>
  <div>
    <div class="subheading mb-7">
      <span class="subheading-dot" />
      <span class="subheading-text">
        {{ t('company_basic_information') }}
      </span>
    </div>
    <div>
      <!-- 資本金 -->
      <div class="row">
        <div class="col-12 col-xl-6 d-md-flex mb-md-4 mb-7">
          <div
            class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
          >
            {{ t("capital") }}
          </div>
          <div class="flex-1">
            <span
              id="capital"
              :class="{ 'text-gray03': !capital }"
              class="font-middle"
            >
              {{ capitalValue }}
            </span>
          </div>
        </div>
        <!-- 従業員数 -->
        <div class="col-12 col-xl-6 d-md-flex mb-md-4 mb-7">
          <div
            class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
          >
            {{ t("number_of_employees") }}
          </div>
          <div class="flex-1">
            <span
              id="number-of-employees"
              :class="{ 'text-gray03': !employees }"
              class="font-middle"
            >
              {{ employeesValue }}
            </span>
          </div>  
        </div>
      </div>
      <!-- 創立年 -->
      <div class="row">
        <div class="col-12 col-xl-6 d-md-flex mb-md-4 mb-7">
          <div
            class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
          >
            {{ t("founding_year") }}
          </div>
          <div class="flex-1">
            <span
              id="founding-year"
              :class="{ 'text-gray03': !foundingYear }"
              class="font-middle"
            >
              {{ foundingYearValue }}
            </span>
          </div>
        </div>
        <div class="col-12 col-xl-6 d-md-flex mb-md-4 mb-7">
          <!-- 代表者 -->
          <div
            class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
          >
            {{ t("representative") }}
          </div>
          <div class="flex-1">
            <span
              id="representative"
              :class="{ 'text-gray03': !representative }"
              class="font-middle"
            >
              {{ representativeValue }}
            </span>
          </div>
        </div>
      </div>
      <!-- 本社所在地 -->
      <div class="d-md-flex mb-md-4 mb-7">
        <div
          class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
        >
          {{ t("head_office_location") }}
        </div>
        <div class="flex-1">
          <span
            id="location"
            :class="{ 'text-gray03': !location }"
            class="font-middle"
          >
            {{ locationValue }}
          </span>
        </div>
      </div>
      <!-- 企業URL -->
      <div class="d-md-flex mb-md-4 mb-7">
        <div
          class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
        >
          {{ t("company_site_url") }}
        </div>
        <div class="flex-1">
          <a
            v-if="url"
            id="company-url-link"
            :href="url"
            target="_blank"
            class="font-middle"
          >
            <span id="company-url">{{ url }}</span>
            <i class="material-icons font-large">
              open_in_new
            </i>
          </a>
          <span
            v-else
            id="company-url"
            class="text-gray03 font-middle"
          >
            {{ t("unknown") }}
          </span>
        </div>
      </div>
      <!-- 関連URL -->
      <div class="d-md-flex mb-md-4 mb-7">
        <div
          class="company-basic-info-label text-black bold d-md-inline-block mb-md-0 mb-1"
        >
          {{ t("related_information") }}
        </div>
        <div
          id="related-information"
          class="flex-1"
        >
          <span
            v-if="Object.keys(relatedInformation).length === 0"
            class="text-gray03 font-middle"
          >
            {{ t("empty") }}
          </span>
          <template
            v-for="(info, key) in relatedInformation"
          >
            <div
              v-if="info"
              :key="key"
              class="mb-1"
            >
              <span
                class="word-pre-wrap font-middle"
                v-html="info"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import I18n from "../../i18n/i18n";
import "../../i18n/ja";
import { i18n } from "../../mixins/i18n";
import axios from "axios";

export default {
  mixins: [i18n],
  props: {
    capital: {
      type: Number,
      default: null
    },
    employees: {
      type: String,
      default: ""
    },
    foundingYear: {
      type: String,
      default: ""
    },
    representative: {
      type: String,
      default: ""
    },
    location: {
      type: String,
      default: ""
    },
    url: {
      type: String,
      default: ""
    },
    relatedInformation: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      innerWidth: window.innerWidth
    };
  },
  computed: {
    isPCWidth() {
      return this.innerWidth >= 768;
    },
    capitalValue() {
      if (this.isCapitalEmpty(this.capital)) {
        return I18n.t("empty");
      }
      return `${this.capital.toLocaleString()}   ${I18n.t("yen")}`;
    },
    employeesValue() {
      return this.checkValue(this.employees, I18n.t("persons"), true);
    },
    foundingYearValue() {
      return this.checkValue(this.foundingYear, I18n.t("year"));
    },
    representativeValue() {
      return this.checkValue(this.representative, "");
    },
    locationValue() {
      return this.checkValue(this.location, "");
    }
  },
  mounted() {
    axios.defaults.headers["X-CSRF-TOKEN"] = document.getElementsByName("csrf-token")[0].content;
    axios.defaults.headers.X_REQUESTED_WITH = "XMLHttpRequest";
    window.addEventListener("resize", this.calcWindowWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calcWindowWidth);
  },
  methods: {
    calcWindowWidth() {
      this.innerWidth = window.innerWidth;
    },
    isCapitalEmpty(value) {
      if (value === 0) {
        return false;
      }
      if (value === null || value === "") {
        return true;
      }
      return false;
    },
    checkValue(value, unit, flg = false) {
      if (value === null || value === "") {
        return I18n.t("empty");
      }
      if (flg) {
        return `${value.toLocaleString()}   ${unit}`;
      }
      return `${value}   ${unit}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.company-basic-info-label {
  width: 145px;
  padding-right: 50px;
  padding-top: 2px;
  @media (max-width: 1199px) {
    padding-right: 42px;
  }
}
</style>
